import { CellRange, GridApi } from "@ag-grid-community/core";
import { CustomStatusPanelProps } from "@ag-grid-community/react";
import { Box, HStack } from "@chakra-ui/layout";
import { DecimalTableCellValue } from "gen/model/decimalTableCellValue";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { IntegerTableCellValue } from "gen/model/integerTableCellValue";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import i18n from "i18n/config";
import { max, min } from "mathjs";
import React, { useCallback, useMemo } from "react";
import { useAgGridEventState } from "shared/components/table/ag/AgGridEventState";
import { useTableBoxContext } from "shared/components/table/box/TableBoxContext";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { decimalZero, sumDecimalStrings } from "shared/utils/DecimalUtils";
import { formatMoneyWithoutCurrencySymbol } from "shared/utils/MoneyUtils";

type AggregatableDataTableTypes =
  | MoneyTableCellValue
  | DecimalTableCellValue
  | IntegerTableCellValue;

const isAggregatableCell = (value: {
  type?: string;
}): value is AggregatableDataTableTypes =>
  ["integer", "decimal", "money"].includes(value.type || "");

const STATS: {
  label: string;
  getStat: (numbers: AggregatableDataTableTypes[]) => string;
}[] = [
  {
    label: i18n.t("common:table.count"),
    getStat: (numbers) => numbers.length.toString(),
  },
  {
    label: i18n.t("common:table.min"),
    getStat: (numbers) =>
      formatMoneyWithoutCurrencySymbol(
        min(
          numbers.map(cellValueForAggregation).map(Number),
          decimalZero(),
        ).toString(),
      ),
  },
  {
    label: i18n.t("common:table.max"),
    getStat: (numbers) =>
      formatMoneyWithoutCurrencySymbol(
        max(
          numbers.map(cellValueForAggregation).map(Number),
          decimalZero(),
        ).toString(),
      ),
  },
  {
    label: i18n.t("common:table.average"),
    getStat: (numbers) =>
      formatMoneyWithoutCurrencySymbol(
        sumDecimalStrings(numbers.map(cellValueForAggregation))
          .dividedBy(numbers.length)
          .toString(),
      ),
  },
  {
    label: i18n.t("common:table.sum"),
    getStat: (numbers) =>
      formatMoneyWithoutCurrencySymbol(
        sumDecimalStrings(numbers.map(cellValueForAggregation)).toString(),
      ),
  },
];

const cellValueForAggregation = (
  cellValue: AggregatableDataTableTypes,
): string | null => {
  switch (cellValue.type) {
    case "money":
      return cellValue.amount;
    case "integer":
      return cellValue.value.toString();
    case "decimal":
      return cellValue.value;
  }
};

const getNumbersInRange: (
  api: GridApi<GenericTableRow>,
) => AggregatableDataTableTypes[] = (api) => {
  const cellRanges = api.getCellRanges();
  // if no selection, clear all the results and do nothing more
  if (!cellRanges || cellRanges.length === 0) {
    return [];
  }
  const sumArray: AggregatableDataTableTypes[] = [];
  // cellRanges is an array of ranges as you can have multiple ranges selected
  cellRanges.forEach((range: CellRange) => {
    // we don't want to handle total rows in ranges
    if (range.startRow!.rowPinned || range.endRow!.rowPinned) {
      return [];
    }
    // get starting and ending row, rowEnd can be before rowStart
    const startRow = Math.min(range.startRow!.rowIndex, range.endRow!.rowIndex);
    const endRow = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex);
    for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
      range.columns.forEach((column) => {
        const rowNode = api.getDisplayedRowAtIndex(rowIndex)!;
        const value = api.getCellValue<GenericTableCell>({
          colKey: column,
          rowNode,
        })?.value;
        // we only want to sum up the values if the cell is aggregatable and the row is not expanded to not double count values
        if (value && isAggregatableCell(value) && !rowNode.expanded) {
          sumArray.push(value);
        }
      });
    }
  });
  return sumArray;
};

const DataTableStatusBarComponent = React.forwardRef<
  CustomStatusPanelProps<GenericTableRow, DataTableContext>
>(() => {
  const { agGrid } = useMaybernTableUIContext();
  const { isFullScreen } = useTableBoxContext();
  const onRangeSelectionChanged = useCallback(getNumbersInRange, []);
  const numbersInRange =
    useAgGridEventState(
      agGrid,
      "rangeSelectionChanged",
      onRangeSelectionChanged,
    ) ?? [];
  const count = numbersInRange.length;
  if (!count) {
    return null;
  }
  return (
    <HStack gap="8px" pb={isFullScreen ? "16px" : undefined}>
      {STATS.map(({ label, getStat }) => (
        <Box className="ag-status-name-value" key={label}>
          <span>{`${label} \u00a0`}</span>
          <span className="ag-status-name-value-value">
            {getStat(numbersInRange)}
          </span>
        </Box>
      ))}
    </HStack>
  );
});

DataTableStatusBarComponent.displayName = "DataTableStatusBarComponent";

export const useDataTableStatusBarProps = (props: DataTableProps) =>
  useMemo(() => {
    if (props.suppressStatusBar) {
      return {};
    }
    return {
      statusBar: {
        statusPanels: [
          {
            statusPanel: DataTableStatusBarComponent,
          },
        ],
      },
    };
  }, [props]);
